// Ported from prototypes/shared/lab.js (the reviewed prototype).
/* The lab: analyses over a set of runs (any date range). Pure functions; browser + Node.
   Every model result carries n and a 95% interval from resampling (whole months or whole runs, so
   runs that are alike don't count as independent). Claims are only made when the interval supports them.
   Revised after a statistical audit: see docs/science-notes.md. */
  const GRADE_LABELS = ['< −8%', '−8 to −5%', '−5 to −3%', '−3 to −1%', 'flat (±1%)', '1 to 3%', '3 to 5%', '5 to 8%', '> 8%'];
  const SPEED_MIN = 1.8, SPEED_STEP = 0.1;
  const median = (xs) => { const s = xs.filter((x) => x != null && isFinite(x)).sort((a, b) => a - b); if (!s.length) return null; const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
  const DAY = 86400000;
  const quantile = (xs, q) => { const s = [...xs].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.max(0, Math.round(q * (s.length - 1))))]; };

  // ---------- least squares + resampling ----------
  function ols(X, y, w) {
    const k = X[0].length, A = Array.from({ length: k }, () => Array(k).fill(0)), b = Array(k).fill(0);
    for (let i = 0; i < X.length; i++) { const wi = w ? w[i] : 1; for (let p = 0; p < k; p++) { b[p] += wi * X[i][p] * y[i]; for (let q = 0; q < k; q++) A[p][q] += wi * X[i][p] * X[i][q]; } }
    for (let c = 0; c < k; c++) {
      let piv = c; for (let r = c + 1; r < k; r++) if (Math.abs(A[r][c]) > Math.abs(A[piv][c])) piv = r;
      [A[c], A[piv]] = [A[piv], A[c]]; [b[c], b[piv]] = [b[piv], b[c]];
      if (Math.abs(A[c][c]) < 1e-10) return null;
      for (let r = 0; r < k; r++) { if (r === c) continue; const f = A[r][c] / A[c][c]; for (let q = c; q < k; q++) A[r][q] -= f * A[c][q]; b[r] -= f * b[c]; }
    }
    return b.map((v, i) => v / A[i][i]);
  }
  function rng(seed) { return () => { seed |= 0; seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
  // resample whole groups (months or runs) with replacement
  function clusterBoot(groups, stat, B = 400, seed = 7) {
    const r = rng(seed), keys = [...groups.keys()], out = [];
    for (let b = 0; b < B; b++) {
      const rows = [];
      for (let i = 0; i < keys.length; i++) { const k = keys[Math.floor(r() * keys.length)]; rows.push(...groups.get(k).map((x) => Object.assign({}, x, { _g: x._g + '#' + i }))); }
      const v = stat(rows); if (v != null && isFinite(v)) out.push(v);
    }
    return out;
  }
  const groupBy = (rows, key) => { const g = new Map(); rows.forEach((x) => { const k = key(x); if (!g.has(k)) g.set(k, []); g.get(k).push(x); }); return g; };
  const ci95 = (xs) => [quantile(xs, 0.025), quantile(xs, 0.975)];

  // ---------- 1. heat, hills: heartbeats per km ----------
  // ln(metres per heartbeat) = temp + climb(m/km) + long-run flag + a separate level for every calendar month
  // (month levels absorb seasonal fitness, so heat isn't confused with time of year).
  // Uncertainty: resample whole months.
  function regRows(runs) {
    return runs.filter((r) => !r.isRace && r.lab && r.lab.beatsPerKm && r.wx && r.distM >= 3000).map((r) => ({
      r, _g: r.date.slice(0, 7), temp: r.wx.temp, climb: r.elevGainM / (r.distM / 1000), long: r.kind === 'long' ? 1 : 0, y: Math.log(1000 / r.lab.beatsPerKm),
    }));
  }
  function fitFE(rows) {
    const months = [...new Set(rows.map((x) => x._g))];
    if (rows.length < months.length + 4) return null;
    const X = rows.map((x) => { const v = [x.temp, x.climb, x.long]; months.forEach((m) => v.push(x._g === m ? 1 : 0)); return v; });
    const b = ols(X, rows.map((x) => x.y));
    return b ? { temp: b[0], climb: b[1], long: b[2] } : null;
  }
  function heatHills(runs) {
    const rows = regRows(runs);
    if (rows.length < 25) return { ok: false, n: rows.length, why: 'needs 25+ regular runs with heart rate and weather' };
    const temps = rows.map((x) => x.temp);
    const iqr = quantile(temps, 0.75) - quantile(temps, 0.25), p10 = quantile(temps, 0.1), p90 = quantile(temps, 0.9);
    const band = { p10, p25: quantile(temps, 0.25), p75: quantile(temps, 0.75), p90 };
    // a few hot or cold days can't qualify a period: the middle 80% of runs must span 7°C+
    if (p90 - p10 < 7) return { ok: false, n: rows.length, band, why: 'temperatures in this period vary too little to separate heat from other effects' };
    const b = fitFE(rows);
    if (!b) return { ok: false, n: rows.length, why: 'too few runs per month to fit the model' };
    const groups = groupBy(rows, (x) => x._g);
    if (groups.size < 4) return { ok: false, n: rows.length, why: 'needs runs spread over at least 4 months' };
    // resample whole months; a month drawn twice gets two separate month levels
    const bootsArr = [];
    const r = rng(11), keys = [...groups.keys()];
    for (let i = 0; i < 300; i++) { const rs = []; for (let j = 0; j < keys.length; j++) { const k = keys[Math.floor(r() * keys.length)]; rs.push(...groups.get(k).map((x) => Object.assign({}, x, { _g: k + '#' + j }))); } const f = fitFE(rs); if (f) bootsArr.push(f); }
    const pct = (coef, delta) => (Math.exp(-coef * delta) - 1) * 100;
    const eff = (key, delta) => ({ est: pct(b[key], delta), ci: ci95(bootsArr.map((bb) => pct(bb[key], delta))) });
    // effect(key, delta) lets the page express effects in the viewer's units (e.g. per 10°F, per 50 ft of climb per mile)
    return { ok: true, n: rows.length, months: groups.size, band, tempRange: [Math.min(...temps), Math.max(...temps)], tempIQR: iqr, heat5: eff('temp', 5), hill10: eff('climb', 10), long: eff('long', 1), effect: eff, coef: b };
  }

  // ---------- 2. economy: heartbeats per km at standard conditions, month by month ----------
  function economy(periodRuns, allRuns) {
    const all = regRows(allRuns);
    const b = all.length >= 40 ? fitFE(all) : null;
    if (!b) return { ok: false, why: 'needs 40+ regular runs with heart rate and weather to estimate the heat and hill adjustments' };
    const adj = (r) => 1000 / Math.exp(Math.log(1000 / r.lab.beatsPerKm) - b.temp * (r.wx.temp - 15) - b.climb * (r.elevGainM / (r.distM / 1000)) - b.long * (r.kind === 'long' ? 1 : 0));
    const rows = regRows(periodRuns).map((x) => ({ run: x.r, adj: adj(x.r) }));
    const byM = groupBy(rows, (x) => x.run.date.slice(0, 7));
    const r = rng(3);
    const series = [...byM.entries()].sort().filter(([, v]) => v.length >= 3).map(([key, v]) => {
      const vals = v.map((x) => x.adj);
      const bs = Array.from({ length: 400 }, () => median(vals.map(() => vals[Math.floor(r() * vals.length)])));
      return { key, v: median(vals), n: v.length, ci: ci95(bs), boots: bs };
    });
    if (series.length < 3) return { ok: false, why: 'needs at least three months with 3+ runs that have heart rate and weather' };
    const best = series.reduce((a, s) => (s.v < a.v ? s : a), series[0]);
    // per year: which month is lowest, and how sure (share of resamples where it stays lowest)
    const years = [...new Set(series.map((s) => s.key.slice(0, 4)))];
    const peaks = years.map((y) => {
      const ys = series.filter((s) => s.key.startsWith(y));
      if (ys.length < 3) return null;
      const lo = ys.reduce((a, s) => (s.v < a.v ? s : a), ys[0]);
      let wins = 0; for (let i = 0; i < 400; i++) { const m = ys.reduce((a, s) => (s.boots[i] < a.boots[i] ? s : a), ys[0]); if (m === lo) wins++; }
      return { year: y, s: lo, prob: wins / 400, months: ys.length };
    }).filter(Boolean);
    const thin = [...byM.entries()].filter(([, v]) => v.length < 3).map(([k]) => k);
    return { ok: true, coef: b, rows, series, best, peaks, thin, adj };
  }

  // ---------- 3. hills: grade curve with within-run comparisons ----------
  function gradeCurve(runs) {
    const use = runs.filter((r) => r.lab && r.lab.gradeBins && !r.isRace);
    const acc = Array.from({ length: 9 }, () => [0, 0, 0, 0, 0, 0, 0]);
    use.forEach((r) => r.lab.gradeBins.forEach((b, i) => b.forEach((v, k) => (acc[i][k] += v))));
    const bins = acc.map((b, i) => ({ label: GRADE_LABELS[i], i, distM: b[0], secs: b[1], walkShare: b[1] ? b[2] / b[1] : null, runPace: b[5] > 200 ? (b[6] / b[5]) * 1000 : null, hr: b[4] ? b[3] / b[4] : null }));
    const flat = bins[4];
    if (use.length < 10 || flat.distM < 5000) return { ok: false, n: use.length };
    // within each run: gentle-descent running pace minus flat running pace (both 300 m+), weighted by the smaller
    const pairs = use.map((r) => { const g = r.lab.gradeBins; const d = g[3], f = g[4]; if (d[5] < 300 || f[5] < 300) return null; return { _g: r.id, diff: (d[6] / d[5] - f[6] / f[5]) * 1000, w: Math.min(d[5], f[5]) }; }).filter(Boolean);
    const wmean = (ps) => ps.reduce((a, p) => a + p.diff * p.w, 0) / ps.reduce((a, p) => a + p.w, 0);
    const descent = pairs.length >= 15 ? { est: wmean(pairs), ci: ci95(clusterBoot(groupBy(pairs, (p) => p._g), wmean, 400)), n: pairs.length, slower: pairs.filter((p) => p.diff > 0).length } : null;
    // walking on descents vs climbs, within each run
    let more = 0, fewer = 0;
    use.forEach((r) => { const g = r.lab.gradeBins; const ws = (idx) => { const s = idx.reduce((a, i) => a + g[i][1], 0); return s > 120 ? idx.reduce((a, i) => a + g[i][2], 0) / s : null; }; const dn = ws([1, 2, 3]), up = ws([5, 6, 7]); if (dn == null || up == null) return; if (dn > up + 0.02) more++; else if (up > dn + 0.02) fewer++; });
    const wsh = (idx) => { const s = idx.reduce((a, i) => a + bins[i].secs, 0); return s ? idx.reduce((a, i) => a + (bins[i].walkShare || 0) * bins[i].secs, 0) / s : null; };
    return { ok: true, n: use.length, bins, flat, descent, walk: { down: wsh([1, 2, 3]), up: wsh([5, 6, 7]), more, fewer } };
  }

  // ---------- 4. cadence & stride: how you speed up ----------
  // Within-run fit: log cadence vs log speed, both centred on each run's own means (so differences
  // between runs, terrain and days don't leak in). Speed = cadence × step, so stride's share is 1 − slope.
  function strideCadence(runs) {
    const use = runs.filter((r) => r.lab && r.lab.speedBins && !r.isRace);
    const pts = [];
    use.forEach((r) => {
      const bs = r.lab.speedBins.map((b, i) => ({ v: SPEED_MIN + (i + 0.5) * SPEED_STEP, secs: b[0], spm: b[0] ? b[1] / b[0] : null })).filter((b) => b.secs >= 30 && b.spm);
      if (bs.length < 3) return;
      const W = bs.reduce((a, b) => a + b.secs, 0);
      const mx = bs.reduce((a, b) => a + Math.log(b.v) * b.secs, 0) / W, my = bs.reduce((a, b) => a + Math.log(b.spm) * b.secs, 0) / W;
      bs.forEach((b) => pts.push({ _g: r.id, x: Math.log(b.v) - mx, y: Math.log(b.spm) - my, w: b.secs }));
    });
    const runsUsed = new Set(pts.map((p) => p._g)).size;
    if (runsUsed < 10) return { ok: false, n: runsUsed };
    const slope = (ps) => { const sxx = ps.reduce((a, p) => a + p.w * p.x * p.x, 0); return sxx ? ps.reduce((a, p) => a + p.w * p.x * p.y, 0) / sxx : null; };
    const a = slope(pts), boots = clusterBoot(groupBy(pts, (p) => p._g), slope, 300);
    // pooled curves for display
    const acc = Array.from({ length: 40 }, () => [0, 0, 0, 0, 0, 0, 0]);
    use.forEach((r) => r.lab.speedBins.forEach((b, i) => b.forEach((v, k) => (acc[i][k] += v))));
    const bins = acc.map((b, i) => ({ v: SPEED_MIN + (i + 0.5) * SPEED_STEP, secs: b[0], spm: b[0] ? b[1] / b[0] : null, step: b[0] ? b[2] / b[0] : null, gct: b[6] ? b[3] / b[6] : null })).filter((b) => b.secs >= 300);
    const ci = ci95(boots);
    return { ok: true, n: runsUsed, bins, cadShare: { est: a, ci }, strideShare: { est: 1 - a, ci: [1 - ci[1], 1 - ci[0]] } };
  }
  // cadence and ground contact at matched speed, month by month: each run's bins near the reference speed
  // are compared with your pooled average at exactly that speed bin, so speed mix can't fake a trend.
  function formTrend(runs, refV) {
    const use = runs.filter((r) => r.lab && r.lab.speedBins && !r.isRace);
    const pool = Array.from({ length: 40 }, () => [0, 0, 0, 0]);
    use.forEach((r) => r.lab.speedBins.forEach((b, i) => { pool[i][0] += b[0]; pool[i][1] += b[1]; pool[i][2] += b[3]; pool[i][3] += b[6] || 0; }));
    const ref = pool.map((p) => ({ spm: p[0] ? p[1] / p[0] : null, gct: p[3] ? p[2] / p[3] : null }));
    const perRun = use.map((r) => {
      let s = 0, ds = 0, g = 0, dg = 0;
      r.lab.speedBins.forEach((b, i) => { const v = SPEED_MIN + (i + 0.5) * SPEED_STEP; if (Math.abs(v - refV) > 0.2 || !b[0] || !ref[i].spm) return; s += b[0]; ds += (b[1] / b[0] - ref[i].spm) * b[0]; if (b[6] && ref[i].gct) { g += b[6]; dg += (b[3] / b[6] - ref[i].gct) * b[6]; } });
      return s >= 120 ? { _g: r.id, month: r.date.slice(0, 7), secs: s, dSpm: ds / s, gSecs: g, dGct: g ? dg / g : null } : null;
    }).filter(Boolean);
    const months = [...groupBy(perRun, (x) => x.month).entries()].sort().map(([month, xs]) => {
      const secs = xs.reduce((a, x) => a + x.secs, 0), gs = xs.reduce((a, x) => a + x.gSecs, 0);
      return { month, runs: xs.length, secs, dSpm: xs.reduce((a, x) => a + x.dSpm * x.secs, 0) / secs, dGct: gs ? xs.reduce((a, x) => a + (x.dGct || 0) * x.gSecs, 0) / gs : null, items: xs };
    }).filter((m) => m.runs >= 3 && m.secs >= 1800);
    let change = null;
    if (months.length >= 6) {
      const early = months.slice(0, 3).flatMap((m) => m.items), late = months.slice(-3).flatMap((m) => m.items);
      const wavg = (xs, k, wk) => { const W = xs.reduce((a, x) => a + (x[k] != null ? x[wk] : 0), 0); return W ? xs.reduce((a, x) => a + (x[k] != null ? x[k] * x[wk] : 0), 0) / W : null; };
      const diff = (k, wk) => (rows) => { const e = rows.filter((x) => x._side === 'e'), l = rows.filter((x) => x._side === 'l'); const a = wavg(e, k, wk), b = wavg(l, k, wk); return a == null || b == null ? null : b - a; };
      const rows = early.map((x) => Object.assign({}, x, { _side: 'e' })).concat(late.map((x) => Object.assign({}, x, { _side: 'l' })));
      const g = groupBy(rows, (x) => x._g + x._side);
      change = {
        from: months.slice(0, 3).map((m) => m.month), to: months.slice(-3).map((m) => m.month),
        spm: { est: diff('dSpm', 'secs')(rows), ci: ci95(clusterBoot(g, diff('dSpm', 'secs'), 300)) },
        gct: { est: diff('dGct', 'gSecs')(rows), ci: ci95(clusterBoot(g, diff('dGct', 'gSecs'), 300)) },
      };
    }
    return { months, change };
  }

  // ---------- 5. VO2max: Garmin's estimate vs your race results ----------
  // Daniels & Gilbert (1979) VDOT: oxygen cost of race velocity ÷ fraction of VO2max sustainable for that duration.
  function vdot(distM, timeS) {
    const t = timeS / 60, v = distM / t;
    const cost = -4.6 + 0.182258 * v + 0.000104 * v * v;
    const frac = 0.8 + 0.1894393 * Math.exp(-0.012778 * t) + 0.2989558 * Math.exp(-0.1932605 * t);
    return cost / frac;
  }
  function vo2Series(runs, races) {
    const byDay = {};
    for (const r of runs) if (r.vo2) byDay[r.date] = r.vo2;
    const est = Object.entries(byDay).map(([date, v]) => ({ date, v }));
    const rv = races.map((x) => ({ date: x.run.date, name: x.run.raceName, nominal: x.nominal, v: vdot(x.nominal, x.t), t: x.t }));
    return { est, races: rv, ok: est.length >= 5 };
  }

  // ---------- 6. heart-rate drift on long training runs (races shown separately) ----------
  function drift(runs) {
    const rows = runs.filter((r) => r.lab && r.lab.decoupling != null && r.movingS >= 5400 && !r.isRace).map((r) => ({ run: r, d: r.lab.decoupling, temp: r.wx ? r.wx.temp : null }));
    const races = runs.filter((r) => r.lab && r.lab.decoupling != null && r.isRace && r.movingS >= 5400).map((r) => ({ run: r, d: r.lab.decoupling, temp: r.wx ? r.wx.temp : null }));
    return { ok: rows.length >= 4, rows, races, median: median(rows.map((x) => x.d)) };
  }

  // ---------- 7. run–walk ----------
  function runWalk(runs) {
    const withB = runs.filter((r) => r.lab && r.lab.breaks);
    const all = withB.flatMap((r) => r.lab.breaks.list.map((b) => ({ run: r, km: b[0] / 1000, dur: b[1], drop: b[2] })));
    const segs = runs.map((r) => r.lab && r.lab.runSegMed).filter(Boolean);
    let perTen = null;
    const fitR = all.filter((b) => b.dur <= 60);
    if (fitR.length >= 30) { const c = ols(fitR.map((b) => [1, b.dur]), fitR.map((b) => b.drop)); if (c) perTen = c[1] * 10; }
    return { ok: all.length >= 30, runs: withB.length, breaks: all.length, medDur: median(all.map((b) => b.dur)), medDrop: median(all.map((b) => b.drop)), medRunSeg: median(segs), perTen, all };
  }

  // ---------- 8. heat bands (descriptive) ----------
  function heatBands(runs) {
    const bands = [[-99, 10, 'under 10°C'], [10, 15, '10–15°C'], [15, 20, '15–20°C'], [20, 25, '20–25°C'], [25, 99, '25°C+']];
    return bands.map(([lo, hi, label]) => {
      const rs = runs.filter((r) => !r.isRace && r.wx && r.wx.temp >= lo && r.wx.temp < hi && r.lab && r.lab.beatsPerKm);
      return { label, n: rs.length, bpk: median(rs.map((r) => r.lab.beatsPerKm)), pace: median(rs.map((r) => r.movingS / (r.distM / 1000))), hr: median(rs.map((r) => r.avgHr)) };
    });
  }

  // ---------- 9. builds: the 16 weeks before each race, side by side ----------
  // Only the target race is left out; other races in the block count as running.
  function builds(allRuns, races) {
    return races.map((x) => {
      const end = x.run.date, start = new Date(Date.parse(end) - 112 * DAY).toISOString().slice(0, 10);
      const rs = allRuns.filter((r) => r.date >= start && r.date < end && r.id !== x.run.id);
      if (rs.length < 8) return null;
      const weeks = Array.from({ length: 16 }, (_, i) => { const ws = new Date(Date.parse(start) + i * 7 * DAY).toISOString().slice(0, 10), we = new Date(Date.parse(start) + (i * 7 + 6) * DAY).toISOString().slice(0, 10); const w = rs.filter((r) => r.date >= ws && r.date <= we); return { ws, km: w.reduce((a, r) => a + r.distM, 0) / 1000, long: w.reduce((a, r) => Math.max(a, r.distM), 0) / 1000, n: w.length }; });
      const longDrift = rs.filter((r) => r.lab && r.lab.decoupling != null && r.movingS >= 5400 && !r.isRace).map((r) => r.lab.decoupling);
      return {
        race: x, weeks, totalKm: weeks.reduce((a, w) => a + w.km, 0), avgKm: weeks.reduce((a, w) => a + w.km, 0) / 16,
        peakKm: Math.max(...weeks.map((w) => w.km)), longest: Math.max(...weeks.map((w) => w.long)), runs: rs.length,
        longRuns: rs.filter((r) => r.distM >= 16000).length, drift: median(longDrift), driftN: longDrift.length,
      };
    }).filter(Boolean);
  }

  // ---------- 10. races side by side (beats per km on the race's official distance, not GPS) ----------
  function raceCompare(races) {
    return races.map((x) => {
      const r = x.run, L = r.lab || {};
      const beats = L.beatsPerKm ? Math.round((L.beatsPerKm * r.distM) / 1000) : null;
      const km = (x.nominal || r.distM) / 1000;
      return {
        race: x, t: x.t, pace: x.t / km, hr: r.avgHr, beats, bpk: beats ? beats / km : null,
        drift: L.decoupling != null ? L.decoupling : null, spm: L.run ? L.run.spm : null, step: L.run ? L.run.step : null,
        walks: L.breaks ? L.breaks.n : null, walkShare: r.walk ? r.walk.walkM / (r.walk.runM + r.walk.walkM) : null, temp: r.wx ? r.wx.temp : null, dew: r.wx ? r.wx.dew : null,
      };
    });
  }

export default { economy, builds, raceCompare, heatHills, gradeCurve, strideCadence, formTrend, vdot, vo2Series, drift, runWalk, heatBands, median, ols, GRADE_LABELS };
